export const SITE_URL = "https://www.kynetic.krd";
export const SITE_NAME = "Kynetic";
export const DEFAULT_IMAGE = `${SITE_URL}/city-skyline.jpg`;
export const CONTACT_EMAIL = "arez.khoshnaw@kynetic.krd";

export const publicRoutes = ["/", "/services", "/process", "/about", "/contact"];

export const pageMetadata = {
  "/": {
    title: "Kynetic | Digital Marketing Agency in Kurdistan",
    description:
      "Kynetic is a growth-focused digital marketing agency helping brands build momentum through strategy, creative campaigns, SEO, social media and web experiences.",
    type: "website",
  },
  "/services": {
    title: "Digital Marketing Services | Kynetic",
    description:
      "Explore Kynetic services including social media management, digital strategy, branding, PPC, video production, SEO, email marketing, web experiences and CRO.",
    type: "website",
  },
  "/process": {
    title: "Kynetic Process | Discovery, Ignition and Momentum",
    description:
      "See how Kynetic turns research, strategy and campaign execution into measurable marketing momentum through discovery, launch and optimization.",
    type: "website",
  },
  "/about": {
    title: "About Kynetic | Marketing in Motion",
    description:
      "Learn about Kynetic, the marketing agency built to move brands beyond static strategies with clear direction, creative force and data-led growth.",
    type: "website",
  },
  "/contact": {
    title: "Contact Kynetic | Start Your Marketing Project",
    description:
      "Contact Kynetic to discuss your marketing goals, website, brand growth, lead generation, creative production or digital strategy needs.",
    type: "website",
  },
  notFound: {
    title: "Page Not Found | Kynetic",
    description:
      "The Kynetic page you are looking for could not be found. Return to the homepage or explore services, process, about and contact pages.",
    type: "website",
    noindex: true,
  },
};

const normalizePath = (path) => {
  if (!path || path === "/") {
    return "/";
  }

  return `/${path.replace(/^\/+|\/+$/g, "")}`;
};

export const getCanonicalUrl = (path) => {
  const normalizedPath = normalizePath(path);
  return `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;
};

export const getMetadataForPath = (path) => {
  const normalizedPath = normalizePath(path);
  return pageMetadata[normalizedPath] || pageMetadata.notFound;
};

export const getBreadcrumbSchema = (path) => {
  const normalizedPath = normalizePath(path);

  if (normalizedPath === "/" || !pageMetadata[normalizedPath]) {
    return null;
  }

  const label = normalizedPath
    .slice(1)
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: label,
        item: getCanonicalUrl(normalizedPath),
      },
    ],
  };
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  email: CONTACT_EMAIL,
  logo: `${SITE_URL}/favicon.ico`,
};

export const professionalServiceSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#professional-service`,
  name: SITE_NAME,
  url: SITE_URL,
  email: CONTACT_EMAIL,
  image: DEFAULT_IMAGE,
  areaServed: [
    {
      "@type": "Place",
      name: "Kurdistan Region",
    },
    {
      "@type": "Country",
      name: "Iraq",
    },
  ],
  serviceType: "Digital marketing agency",
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
};

export const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${SITE_URL}/services#services`,
  name: "Kynetic Digital Marketing Services",
  itemListElement: [
    "Social Media Management",
    "Digital Strategy & Consulting",
    "Influencer & Creator Marketing",
    "Branding & Identity Design",
    "Performance Marketing",
    "Video Production",
    "Web Experience & CRO",
    "Search Engine Optimization",
    "Email Marketing & Automation",
    "Marketing Operations & AI Integration",
    "Product Photography",
  ].map((name, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Service",
      name,
      provider: {
        "@id": `${SITE_URL}/#organization`,
      },
      areaServed: "Kurdistan Region and Iraq",
    },
  })),
};

export const getSchemasForPath = (path) => {
  const normalizedPath = normalizePath(path);
  const schemas = [organizationSchema, websiteSchema];
  const breadcrumbSchema = getBreadcrumbSchema(normalizedPath);

  if (normalizedPath === "/") {
    schemas.push(professionalServiceSchema);
  }

  if (normalizedPath === "/services") {
    schemas.push(servicesSchema);
  }

  if (breadcrumbSchema) {
    schemas.push(breadcrumbSchema);
  }

  return schemas;
};
